import type { Modifiers } from '@engine/types'
import { modifyCasing } from '@utils/text'

// Carries the signal base and any loop-local aliases that a rewritten Rocket
// subtree should resolve against.
//
// Rocket templates author against `$$foo`, `block`, `i`, etc. Datastar only
// understands concrete signal paths, so every rewrite pass needs both the
// component's private base path and any local alias remaps in scope.
export type TaggedLiteralScope = {
  signalPathBase: string
  localSignals?: Record<string, string>
}

export type TaggedLiteral = (
  strings: TemplateStringsArray,
  ...values: unknown[]
) => DocumentFragment

// Minimal parse modes for the string chunks inside a tagged template.
//
// Rocket needs to know where each interpolation lands before it can decide how
// to encode it. `${value}` inside text content, quoted attributes, unquoted
// attributes, and raw-text tags all need different escaping rules, and DOM
// nodes are only legal in normal data positions.
type TemplateContext =
  | 'data'
  | 'tag'
  | 'attrDouble'
  | 'attrSingle'
  | 'attrUnquoted'
  | 'rawtext'

// Stateful scanner carried across template string chunks.
//
// Tagged templates split the authored source around interpolations, so Rocket
// cannot inspect one chunk in isolation and know whether the next `${...}` is
// inside `class="..."`, `<script>...</script>`, or normal text. This state
// machine tracks just enough HTML parsing context to make interpolation
// decisions without needing a full parser before DOM creation.
type TemplateScanState = {
  context: TemplateContext
  tagName: string
  closingTag: boolean
  captureTagName: boolean
  rawTextTag: string
}

const rawTextTags = new Set(['script', 'style', 'textarea', 'title'])

// Tagged templates can interpolate Nodes and nested iterables of Nodes/text.
// Append them into a fragment here so callers can treat `${child}` and
// `${items.map(...)}` as first-class DOM composition instead of serializing
// them back through HTML text.
export const appendComposedTemplateValue = (
  fragment: DocumentFragment,
  value: unknown,
) => {
  if (value == null || value === false) return
  if (value instanceof Node) {
    fragment.appendChild(value)
    return
  }
  for (const item of value as Iterable<unknown>) {
    if (item == null || item === false) continue
    if (item instanceof Node) {
      fragment.appendChild(item)
      continue
    }
    fragment.appendChild(document.createTextNode(stringifyTemplateValue(item)))
  }
}

// Build one HTML or SVG fragment from a tagged template in two phases:
//
// 1. join the raw string chunks and primitive interpolations into one markup
//    string, escaping each primitive according to where it lands
// 2. parse that markup with `render(...)`, then replace comment markers with
//    real DOM-valued interpolations before `postprocess(...)`
//
// Rocket needs this split pipeline because its templates allow all of these
// at once:
// - primitives in text or attribute positions
// - boolean-style whole-attribute expressions like `disabled=${flag}`
// - DOM nodes and nested iterables like `${children}`
// - a final rewrite pass that scopes Datastar attributes after parsing
//
// A plain `innerHTML` write only handles the primitive-string half. It
// cannot safely express "insert this real Node here" without serializing it
// back to text, and it cannot recover enough context after the fact to know
// whether an interpolation originally occupied a whole attribute slot. This
// helper keeps those cases precise while still letting the browser do the
// actual HTML/SVG parsing.
export function hypertext(
  render: (string: string) => ParentNode,
  postprocess: (root: ParentNode) => DocumentFragment,
) {
  return ({ raw: strings }: TemplateStringsArray, ...values: unknown[]) => {
    const state: TemplateScanState = {
      context: 'data',
      tagName: '',
      closingTag: false,
      captureTagName: false,
      rawTextTag: '',
    }
    let string = ''

    for (let j = 0; j < strings.length; ++j) {
      let input = strings[j]
      if (j > 0) {
        const value = values[j - 1]
        if (
          value instanceof Node ||
          (typeof value === 'object' &&
            value !== null &&
            Symbol.iterator in value)
        ) {
          // Real DOM values only make sense in normal data positions. When
          // they appear there, emit a marker and splice the actual content back
          // in after the browser has parsed the surrounding HTML string.
          if (state.context !== 'data') {
            throw new Error('invalid template composition')
          }
          string += `<!--::${j}-->`
        } else if (
          value == null ||
          value instanceof Date ||
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          typeof value === 'bigint'
        ) {
          // A falsy data-position interpolation should contribute no text, but the
          // following string chunk still needs to be scanned and appended.
          if (value === false && state.context === 'data') {
          } else {
            const previous = strings[j - 1]
          const doubleQuotedAttribute = previous.match(/(\s+[^\s"'<>/=]+)="$/)
          const singleQuotedAttribute = previous.match(/(\s+[^\s"'<>/=]+)='$/)
          const unquotedAttribute =
            doubleQuotedAttribute || singleQuotedAttribute
              ? null
              : previous.match(/(\s+[^\s"'<>/=]+)=$/)
          const wholeAttribute =
            (doubleQuotedAttribute && input.startsWith('"')) ||
            (singleQuotedAttribute && input.startsWith("'")) ||
            (unquotedAttribute && (input === '' || /^[\s/>]/.test(input)))
          // Whole-attribute interpolation means the expression owns the full
          // attribute value position, as in `disabled=${flag}` or
          // `class="${name}"`. That lets Rocket preserve HTML boolean-attribute
          // semantics instead of stringifying `true`, `false`, or `null`.
          const context = doubleQuotedAttribute
            ? 'attrDouble'
            : singleQuotedAttribute
              ? 'attrSingle'
              : unquotedAttribute
                ? 'attrUnquoted'
                : state.context
          const text =
            wholeAttribute &&
            (value == null || value === false || value === '' || value === true)
              ? ''
              : (() => {
                  const text = stringifyTemplateValue(value)
                  switch (context) {
                    case 'attrDouble':
                      return text.replace(/["&]/g, entity)
                    case 'attrSingle':
                      return text.replace(/['&]/g, entity)
                    case 'attrUnquoted':
                      return text === ''
                        ? "''"
                        : text.replace(/[\s>&]/g, entity)
                    case 'rawtext':
                      return text.replace(/[<]/g, entity)
                    default:
                      return text.replace(/[<&]/g, entity)
                  }
                })()
          if (
            wholeAttribute &&
            (value == null || value === false || value === '')
          ) {
            string = string.slice(
              0,
              -(
                doubleQuotedAttribute?.[0].length ??
                singleQuotedAttribute?.[0].length ??
                unquotedAttribute?.[0].length ??
                0
              ),
            )
            if (doubleQuotedAttribute || singleQuotedAttribute)
              input = input.slice(1)
          } else if (wholeAttribute && value === true) {
            string =
              string.slice(
                0,
                -(
                  doubleQuotedAttribute?.[0].length ??
                  singleQuotedAttribute?.[0].length ??
                  unquotedAttribute?.[0].length ??
                  0
                ),
              ) +
              (doubleQuotedAttribute?.[1] ??
                singleQuotedAttribute?.[1] ??
                unquotedAttribute?.[1] ??
                '')
            if (doubleQuotedAttribute || singleQuotedAttribute)
              input = input.slice(1)
            } else {
              string += text
            }
          }
        } else {
          throw new Error('invalid template value')
        }
      }
      // Advance the lightweight scanner across this chunk so the next
      // interpolation knows whether it lands in text, a tag, an attribute, or
      // a raw-text element like `<script>`.
      for (let i = 0; i < input.length; i += 1) {
        const char = input[i]
        switch (state.context) {
          case 'data':
            if (
              state.rawTextTag &&
              char === '<' &&
              input
                .slice(i + 1, i + 2 + state.rawTextTag.length)
                .toLowerCase() === `/${state.rawTextTag}` &&
              (!input[i + 2 + state.rawTextTag.length] ||
                /[\s/>]/.test(input[i + 2 + state.rawTextTag.length] ?? ''))
            ) {
              state.context = 'tag'
              state.tagName = ''
              state.closingTag = true
              state.captureTagName = false
              continue
            }
            if (char === '<') {
              state.context = 'tag'
              state.tagName = ''
              state.closingTag = input[i + 1] === '/'
              state.captureTagName = !state.closingTag
            }
            break
          case 'rawtext':
            if (
              char === '<' &&
              input
                .slice(i + 1, i + 2 + state.rawTextTag.length)
                .toLowerCase() === `/${state.rawTextTag}` &&
              (!input[i + 2 + state.rawTextTag.length] ||
                /[\s/>]/.test(input[i + 2 + state.rawTextTag.length] ?? ''))
            ) {
              state.context = 'tag'
              state.tagName = ''
              state.closingTag = true
              state.captureTagName = false
            }
            break
          case 'tag':
            if (!state.tagName) {
              if (char === '!' || char === '?') {
                state.context = 'data'
                state.closingTag = false
                state.captureTagName = false
                continue
              }
              if (state.closingTag && char === '/') continue
              if (/\s/.test(char)) continue
              if (char === '>') {
                state.context = state.rawTextTag ? 'rawtext' : 'data'
                state.closingTag = false
                state.captureTagName = false
                continue
              }
              state.tagName = char.toLowerCase()
              continue
            }
            if (char && !/[\s/>]/.test(char)) {
              if (state.captureTagName) state.tagName += char.toLowerCase()
              continue
            }
            if (char === '>') {
              if (state.closingTag) {
                if (state.tagName === state.rawTextTag) state.rawTextTag = ''
                state.context = 'data'
              } else if (rawTextTags.has(state.tagName)) {
                state.rawTextTag = state.tagName
                state.context = 'rawtext'
              } else {
                state.context = 'data'
              }
              state.closingTag = false
              state.captureTagName = false
              continue
            }
            if (char === '"') {
              state.context = 'attrDouble'
              continue
            }
            if (char === "'") {
              state.context = 'attrSingle'
              continue
            }
            if (char === '=') continue
            if (!/\s/.test(char) && char !== '/') {
              state.context = 'attrUnquoted'
            }
            break
          case 'attrDouble':
            if (char === '"') state.context = 'tag'
            break
          case 'attrSingle':
            if (char === "'") state.context = 'tag'
            break
          case 'attrUnquoted':
            if (/\s/.test(char)) state.context = 'tag'
            else if (char === '>') {
              if (state.closingTag) {
                if (state.tagName === state.rawTextTag) state.rawTextTag = ''
                state.context = 'data'
              } else if (rawTextTags.has(state.tagName)) {
                state.rawTextTag = state.tagName
                state.context = 'rawtext'
              } else {
                state.context = 'data'
              }
              state.closingTag = false
              state.captureTagName = false
            }
            break
        }
      }
      string += input
    }
    const root = render(string)

    // Composed Node/iterable values are emitted as comment markers during the
    // string pass, then spliced back into the parsed fragment here so mixed
    // string/DOM templates still produce one real fragment tree.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT)
    const removeNodes: Comment[] = []
    while (walker.nextNode()) {
      const node = walker.currentNode as Comment
      if (!/^::/.test(node.data)) continue
      const parent = node.parentNode
      if (!parent) continue
      const fragment = document.createDocumentFragment()
      appendComposedTemplateValue(fragment, values[+node.data.slice(2) - 1])
      parent.insertBefore(fragment, node)
      removeNodes.push(node)
    }
    for (const node of removeNodes) node.remove()
    return postprocess(root as DocumentFragment)
  }
}

export const signalNameAttributes = new Set([
  'bind',
  'computed',
  'indicator',
  'ref',
  'signals',
])

const directSignalValuePlugins = new Set(['bind', 'indicator', 'ref'])
const rootModifierPattern = /__root\b/g

export const rocketDispatchActionName = 'dispatchRocket'
export const rocketRefAttr = 'data-rocket-ref'

const parseTemplateModifiers = (raw: string): Modifiers => {
  const mods: Modifiers = new Map()
  for (const rawModifier of raw.split('__').slice(1)) {
    const [name, ...values] = rawModifier.split('.')
    mods.set(name, new Set(values))
  }
  return mods
}

const toRocketRefName = (raw: string) => {
  const name = raw.split('__', 1)[0]
  return modifyCasing(name, parseTemplateModifiers(raw))
}

// Convert Rocket-authored expressions into plain Datastar expressions so the
// engine can evaluate local `$$...` state, loop aliases, and local actions.
const rewriteDatastarExpression = (
  value: string,
  { signalPathBase, localSignals = {} }: TaggedLiteralScope,
) => {
  let next = value.replace(
    /\$\$([a-zA-Z_\d]\w*(?:[.-]\w+)*)/g,
    (_, name: string) => `$.${signalPathBase}.${name}`,
  )
  for (const [name, path] of Object.entries(localSignals)) {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Datastar expressions can be whole template literals. Rewrite local loop
    // aliases inside `${...}` without touching plain quoted text outside them.
    next = next.replace(
      new RegExp(
        `("(?:\\\\.|[^"\\\\])*"|'(?:\\\\.|[^'\\\\])*'|\`(?:\\\\.|[^\`\\\\$]|\\$(?!\\{))*\`)|\\$\\{([^{}]*)\\}|(?<![\\w$.])${escapedName}(?![\\w$]|\\s*:)`,
        'g',
      ),
      (
        match,
        quoted: string | undefined,
        interpolationExpr: string | undefined,
      ) => {
        if (quoted) return match
        if (interpolationExpr !== undefined) {
          return (
            '${' +
            interpolationExpr.replace(
              new RegExp(`(?<![\\w$.])${escapedName}(?![\\w$]|\\s*:)`, 'g'),
              `$.${path}`,
            ) +
            '}'
          )
        }
        return `$.${path}`
      },
    )
  }
  return next.replace(
    /@([A-Za-z_$][\w$]*)\(/g,
    (_, name: string) =>
      `@${rocketDispatchActionName}(${JSON.stringify(name)},`,
  )
}

// Re-scope Datastar attributes from Rocket-authored aliases like `$$count`,
// loop locals, and `@foo(...)` into the concrete signal/action paths that
// Datastar actually executes against for this subtree instance.
export const rewriteDataAttributes = (
  root: ParentNode,
  scope?: TaggedLiteralScope,
) => {
  if (!scope) return
  const { signalPathBase, localSignals = {} } = scope
  const nodes =
    root instanceof Element
      ? [root, ...root.querySelectorAll('*')]
      : [...root.children, ...root.querySelectorAll('*')]
  for (const node of nodes) {
    for (const attr of [...node.attributes]) {
      if (attr.name === 'data-signals') {
        if (node.hasAttribute(`data-signals:${signalPathBase}`)) {
          node.removeAttribute(attr.name)
          continue
        }
        node.removeAttribute(attr.name)
        node.setAttribute(`data-signals:${signalPathBase}`, attr.value)
        continue
      }
      if (attr.name.startsWith('data-signals:')) {
        const signalName = attr.name.slice('data-signals:'.length)
        if (
          signalName === signalPathBase ||
          signalName.startsWith(`${signalPathBase}.`)
        ) {
          continue
        }
        node.removeAttribute(attr.name)
        node.setAttribute(
          `data-signals:${signalPathBase}.${signalName}`,
          attr.value,
        )
        continue
      }
      if (!attr.name.startsWith('data-')) continue
      const attrBody = attr.name.slice('data-'.length)
      const colonIndexInBody = attrBody.indexOf(':')
      const pluginSegment =
        colonIndexInBody === -1 ? attrBody : attrBody.slice(0, colonIndexInBody)
      const attrBodyWithoutMods = pluginSegment.split('__', 1)[0]
      const pluginName = attrBodyWithoutMods.split(':', 1)[0]
      if (pluginName === 'ref') {
        const colonIndex = attr.name.indexOf(':')
        const refName =
          colonIndex !== -1
            ? toRocketRefName(attr.name.slice(colonIndex + 1))
            : toRocketRefName(attr.value)
        node.removeAttribute(attr.name)
        if (refName) {
          node.setAttribute(rocketRefAttr, refName)
        }
        continue
      }
      if (signalNameAttributes.has(pluginName)) {
        const colonIndex = attr.name.indexOf(':')
        if (colonIndex !== -1) {
          const keyWithMods = attr.name.slice(colonIndex + 1)
          const key = keyWithMods.split('__', 1)[0]
          const usesRoot = keyWithMods.includes('__root')
          if (usesRoot) {
            const nextValue = rewriteDatastarExpression(attr.value, scope)
            const nextName = `${attr.name
              .slice(0, colonIndex + 1)
              .replace(rootModifierPattern, '')}${keyWithMods.replace(rootModifierPattern, '')}`
            if (nextName !== attr.name) {
              node.removeAttribute(attr.name)
              node.setAttribute(nextName, nextValue)
            } else if (nextValue !== attr.value) {
              node.setAttribute(attr.name, nextValue)
            }
            continue
          }
          const localSignal = localSignals[key]
          if (localSignal) {
            const nextValue = rewriteDatastarExpression(attr.value, scope)
            if (
              attr.name !==
              `${attr.name.slice(0, colonIndex + 1)}${localSignal}`
            ) {
              node.removeAttribute(attr.name)
              node.setAttribute(
                `${attr.name.slice(0, colonIndex + 1)}${localSignal}${keyWithMods.slice(key.length)}`,
                nextValue,
              )
            } else if (nextValue !== attr.value) {
              node.setAttribute(attr.name, nextValue)
            }
            continue
          }
          const scopedKeyWithMods = key.startsWith(`${signalPathBase}.`)
            ? keyWithMods
            : keyWithMods.replace(key, `${signalPathBase}.${key}`)
          const nextValue = rewriteDatastarExpression(attr.value, scope)
          if (scopedKeyWithMods !== keyWithMods) {
            node.removeAttribute(attr.name)
            node.setAttribute(
              `${attr.name.slice(0, colonIndex + 1)}${scopedKeyWithMods}`,
              nextValue,
            )
          } else if (nextValue !== attr.value) {
            node.setAttribute(attr.name, nextValue)
          }
          continue
        }
        if (directSignalValuePlugins.has(pluginName)) {
          const usesRoot = attrBody.includes('__root')
          if (usesRoot) {
            const nextName = attr.name.replace(rootModifierPattern, '')
            if (nextName !== attr.name) {
              node.removeAttribute(attr.name)
              node.setAttribute(nextName, attr.value)
            }
            continue
          }
          if (pluginName === 'ref' && attr.value) {
            if (
              attr.value === signalPathBase ||
              attr.value.startsWith(`${signalPathBase}.`)
            ) {
              continue
            }
            const localSignal = localSignals[attr.value]
            if (localSignal) {
              node.setAttribute(attr.name, localSignal)
              continue
            }
            node.setAttribute(attr.name, `${signalPathBase}.${attr.value}`)
            continue
          }
          const localSignal = localSignals[attr.value]
          if (localSignal) {
            node.setAttribute(attr.name, localSignal)
            continue
          }
          if (attr.value.startsWith('$$')) {
            node.setAttribute(
              attr.name,
              `${signalPathBase}.${attr.value.slice(2)}`,
            )
          }
          continue
        }
      }
      const nextValue = rewriteDatastarExpression(attr.value, scope)
      if (nextValue === attr.value) continue
      node.setAttribute(attr.name, nextValue)
    }
  }
}

// Encode one character as HTML so primitive interpolations cannot terminate
// the surrounding attribute, tag, or text context.
function entity(character: string) {
  return `&#${character.charCodeAt(0).toString()};`
}

// Convert supported primitive template values into text before the browser
// parses the joined markup string back into DOM.
function stringifyTemplateValue(value: unknown) {
  if (value instanceof Date) return value.toISOString()
  switch (typeof value) {
    case 'string':
      return value
    case 'number':
    case 'boolean':
    case 'bigint':
      return `${value}`
    case 'object':
      return JSON.stringify(value)
    default:
      throw new Error('invalid template value')
  }
}
